import { BaseClient } from '../BaseClient';
import { Empty } from '../compiled-protos/common';
import {
  FeatureFlag,
  FeatureFlagChange,
  FeatureFlagChangeList,
  FeatureFlagList,
  FlagDefinition,
  FlagState,
  KeepFlagRequest,
  ListFlagChangesRequest,
  SetFlagOwnersRequest,
  SetFlagRequest,
} from '../compiled-protos/feature_flag';
import { FeatureFlagServiceClient } from '../compiled-protos/feature_flag.client';

/**
 * Feature flag state. Every method lets its error throw: a caller that can't
 * tell "no flags are on" from "the request failed" can't fall back to the old
 * code on purpose, and the admin page has to show why a write was refused
 * (outside prod every write returns FailedPrecondition by design).
 */
class FeatureFlagClient extends BaseClient {
  self: FeatureFlagServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new FeatureFlagServiceClient(this.transport);
  }

  /** Every flag row, retired ones included, plus the backend registry. */
  public async ListFlags() {
    return await this.self.listFlags(Empty.create(), this.getMetaData()).response;
  }

  /** Turns a flag on or off for everyone. Owners or FeatureFlagAdmin; prod only. */
  public async SetFlag(key: string, state: FlagState) {
    return await this.self.setFlag(SetFlagRequest.create({ key, state }), this.getMetaData())
      .response;
  }

  /**
   * Holds an on flag past its cleanup date. `keepUntil` is YYYY-MM-DD, after
   * today and at most 90 days out; the reason is required.
   */
  public async KeepFlag(key: string, keepUntil: string, keepReason: string) {
    return await this.self.keepFlag(
      KeepFlagRequest.create({ key, keepUntil, keepReason }),
      this.getMetaData(),
    ).response;
  }

  /** Replaces the flag's owner list. FeatureFlagAdmin only; employees, at most 20. */
  public async SetFlagOwners(key: string, ownerUserIds: number[]) {
    return await this.self.setFlagOwners(
      SetFlagOwnersRequest.create({ key, ownerUserIds }),
      this.getMetaData(),
    ).response;
  }

  /** The flag's change log, newest first. Owners or FeatureFlagAdmin. */
  public async ListFlagChanges(key: string) {
    return await this.self.listFlagChanges(
      ListFlagChangesRequest.create({ key }),
      this.getMetaData(),
    ).response;
  }
}

export {
  FeatureFlag,
  FeatureFlagChange,
  FeatureFlagChangeList,
  FeatureFlagClient,
  FeatureFlagList,
  FlagDefinition,
  FlagState,
  KeepFlagRequest,
  ListFlagChangesRequest,
  SetFlagOwnersRequest,
  SetFlagRequest,
};
